import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Hexagon, ArrowLeft, Loader2, Dumbbell, Trophy, Swords, Salad, Cpu,
  ChevronDown, ExternalLink, Search,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const ICONS = { dumbbell: Dumbbell, trophy: Trophy, swords: Swords, salad: Salad, cpu: Cpu };

export default function Library() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(true);
  const [open, setOpen] = useState("calisthenics");
  const [q, setQ] = useState("");

  useEffect(() => { if (!loading && !user) navigate("/auth"); }, [loading, user, navigate]);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/library"); setCats(data.categories); } catch { /* */ } finally { setBusy(false); }
    })();
  }, []);

  const match = (s) => !q || s.toLowerCase().includes(q.toLowerCase());

  if (busy) return <div className="h-screen grid-bg flex items-center justify-center"><Loader2 className="animate-spin text-cyan-400" size={30} /></div>;

  return (
    <div className="min-h-screen bg-[#050505] grid-bg">
      <div className="fixed inset-0 glow-radial pointer-events-none" />
      <header className="relative z-10 flex items-center justify-between px-5 sm:px-8 py-4 border-b border-blue-900/30 backdrop-blur">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-cyan-300" data-testid="back-btn"><ArrowLeft size={18} /></button>
          <div className="flex items-center gap-2">
            <Hexagon className="text-cyan-400" size={18} />
            <div>
              <p className="font-display font-semibold leading-tight">The Library</p>
              <p className="mono-label">ARCHON KNOWLEDGE BASE</p>
            </div>
          </div>
        </div>
      </header>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 bg-slate-950/60 border border-blue-900/50 focus-within:border-cyan-400 rounded-xl px-4 py-2.5 mb-8 transition-all">
          <Search size={16} className="text-slate-500" />
          <input data-testid="library-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search skills, ranks, diets…" className="flex-1 bg-transparent outline-none text-slate-50 placeholder-slate-500" />
        </div>

        <div className="space-y-4">
          {cats.map((cat) => {
            const Icon = ICONS[cat.icon] || Cpu;
            const isOpen = open === cat.id;
            return (
              <div key={cat.id} className="glass rounded-2xl overflow-hidden" data-testid={`category-${cat.id}`}>
                <button onClick={() => setOpen(isOpen ? "" : cat.id)}
                  className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-white/5 transition-colors" data-testid={`cat-toggle-${cat.id}`}>
                  <span className="w-11 h-11 rounded-xl grid place-items-center bg-cyan-400/10 border border-cyan-400/30 shrink-0">
                    <Icon className="text-cyan-300" size={20} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-display text-lg font-semibold">{cat.title}</span>
                    <span className="block text-sm text-slate-400">{cat.desc}</span>
                  </span>
                  <ChevronDown size={20} className={`text-slate-400 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>

                {isOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
                    className="px-5 pb-5 border-t border-blue-900/20 pt-4">
                    {cat.groups ? (
                      <div className="space-y-5">
                        {cat.groups.map((g) => {
                          const items = g.items.filter((it) => match(it.name));
                          if (!items.length) return null;
                          return (
                            <div key={g.name}>
                              <p className="mono-label mb-2">{g.name}</p>
                              <div className="flex flex-wrap gap-2">
                                {items.map((it, i) => (
                                  <span key={i} className="text-sm text-slate-200 border border-blue-900/40 rounded-full px-3 py-1.5" data-testid="lib-item">{it.name}</span>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                        {cat.links && (
                          <div className="flex flex-wrap gap-2 pt-2">
                            {Object.entries(cat.links).map(([k, url]) => (
                              <a key={k} href={url} target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-1 text-xs text-cyan-300 border border-blue-900/40 rounded-full px-3 py-1.5 hover:bg-cyan-400/10 transition-all">
                                {k} <ExternalLink size={11} />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="grid sm:grid-cols-2 gap-3">
                        {cat.items.filter((it) => match(it.name) || match(it.detail || "")).map((it, i) => (
                          <div key={i} className="border border-blue-900/30 rounded-lg px-4 py-3" data-testid="lib-item">
                            <p className="font-display font-semibold text-slate-100">{it.name}</p>
                            {it.detail && <p className="text-sm text-slate-400 mt-0.5">{it.detail}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
